import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import api from '../../config/axiosConfig';
import 'react-toastify/dist/ReactToastify.css';
import { API_URLS } from '../../config/Constants';
import { showErrorMsg, showSuccessMsg } from '../../config/common';
import { sessionManager } from '../../config/sessionManager';
import { TIMEZONES } from '../../config/Constants';
import { Modal, ModalTitle, ModalContent, ModalActions } from '../basic';
import { Role } from '../../types';
import { PermissionsEditor } from '../common/PermissionsEditor';
import { UserPermissions, fetchDefaultPermissionsFromBackend, getAssignableRoles } from '../../config/permissions';

const ROLE_DESCRIPTIONS: Record<string, string> = {
  headCoach: 'Complete admin permissions.',
  assistantCoach: 'Most admin permissions. No editing stats after they lock.',
  statManager: 'Most admin permissions. No editing stats after they lock. No access to KPIs.',
  statTaker: 'Only permissions related to capturing game data.',
};

type AccountType = 'client' | 'employee';

interface AddUserModalProps {
  onClose: () => void;
  onUserAdded?: () => void;
  editUser?: any | null;
  showClientSelection?: boolean;
  defaultClientId?: string;
  preferredAccountType?: AccountType;
}

const createUserSchema = (showClientSelection: boolean, accountType: AccountType) =>
  z
    .object({
      email: z.string().min(1, 'Email is required').email('Please enter a valid email address'),
      firstName: z.string().min(1, 'First name is required').trim(),
      lastName: z.string().min(1, 'Last name is required').trim(),
      phone: z.string().min(1, 'Phone number is required'),
      timezone: z.string().min(1, 'Timezone is required'),
      active: z.boolean(),
      clientId: z.string().optional(),
      permissions: z
        .object({
          editUsers: z.boolean(),
          editKpis: z.boolean(),
          editPlayers: z.boolean(),
          editOpponents: z.boolean(),
          editSeasons: z.boolean(),
          editGames: z.boolean(),
        })
        .optional(),
      clientRoles: z.array(z.string()).optional(),
      employeeRole: z.string().optional(),
    })
    .refine(
      (data) => {
        if (accountType === 'client' && showClientSelection) {
          return !!data.clientId;
        }
        return true;
      },
      {
        message: 'Please select a client',
        path: ['clientId'],
      },
    )
    .refine(
      (data) => {
        if (accountType === 'client') {
          return !!(data.clientRoles && data.clientRoles.length > 0);
        }
        return !!data.employeeRole;
      },
      {
        message: 'Please select a role for this user',
        path: accountType === 'client' ? ['clientRoles'] : ['employeeRole'],
      },
    );

const fieldClass = (hasError?: boolean) =>
  `w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-green-500 ${
    hasError ? 'border-red-500' : 'border-gray-300'
  }`;

const AddUserModal: React.FC<AddUserModalProps> = ({
  onClose,
  onUserAdded,
  editUser,
  showClientSelection = true,
  defaultClientId,
  preferredAccountType = 'client',
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [clients, setClients] = useState<any[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [customPermissions, setCustomPermissions] = useState<Partial<UserPermissions> | null>(null);
  const [showAdvancedPermissions, setShowAdvancedPermissions] = useState(false);
  const [backendDefaultPermissions, setBackendDefaultPermissions] = useState<Record<string, UserPermissions> | null>(
    null,
  );
  const [currentUserPermissions, setCurrentUserPermissions] = useState<Partial<UserPermissions> | null>(null);
  const [accountType, setAccountType] = useState<AccountType>(() =>
    editUser
      ? editUser.clientId
        ? 'client'
        : 'employee'
      : sessionManager.canManageEmployees() && preferredAccountType === 'employee'
        ? 'employee'
        : 'client',
  );

  const isHeadCoach = sessionManager.isHeadCoach();
  const isAssistantCoach = sessionManager.isAssistantCoach();
  const isEmployee = sessionManager.isEmployee();
  const hasFullUserPermissions =
    sessionManager.canPerformAction('users', 'add') &&
    sessionManager.canPerformAction('users', 'edit') &&
    sessionManager.canPerformAction('users', 'delete');
  const canManagePermissions = isHeadCoach || isAssistantCoach || isEmployee || hasFullUserPermissions;
  const canManageEmployees = sessionManager.canManageEmployees();
  const canChooseAccountType = showClientSelection && canManageEmployees && !editUser;

  const initialAccountType: AccountType = editUser
    ? editUser.clientId
      ? 'client'
      : 'employee'
    : canManageEmployees && preferredAccountType === 'employee'
      ? 'employee'
      : 'client';

  const isEditingHeadCoach = useMemo(() => {
    if (!editUser || !editUser.clientRoles || editUser.clientRoles.length === 0) {
      return false;
    }
    const editUserRole = roles.find((r) => r._id === editUser.clientRoles[0]);
    return editUserRole?.value === 'headCoach';
  }, [editUser, roles]);

  const canEditThisUser = useMemo(() => {
    if (!editUser) return true;
    if (isEmployee) return true;
    if (isHeadCoach) return true;
    if (isEditingHeadCoach) return false;
    return true;
  }, [editUser, isEmployee, isHeadCoach, isEditingHeadCoach]);

  const assignableRoles = useMemo(() => {
    if (isEmployee) {
      return roles.filter((role) => role.type === 'client');
    }

    const currentUserRole = sessionManager.getClientRole();
    if (!currentUserRole || !currentUserRole.value) {
      return roles.filter((role) => role.type === 'client');
    }

    const assignableRoleValues = getAssignableRoles(currentUserRole.value);
    return roles.filter((role) => role.type === 'client' && assignableRoleValues.includes(role.value));
  }, [roles, isEmployee]);

  const employeeRoles = useMemo(() => roles.filter((role) => role.type === 'employee'), [roles]);

  const userSchema = useMemo(
    () => createUserSchema(showClientSelection, accountType),
    [showClientSelection, accountType],
  );
  type UserFormData = z.infer<typeof userSchema>;

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    control,
    reset,
    watch,
  } = useForm<UserFormData>({
    resolver: zodResolver(userSchema),
    defaultValues: {
      email: '',
      firstName: '',
      lastName: '',
      phone: '',
      timezone: 'alaska',
      active: true,
      clientId: '',
      permissions: {
        editUsers: false,
        editKpis: false,
        editPlayers: false,
        editOpponents: false,
        editSeasons: false,
        editGames: false,
      },
      clientRoles: [],
      employeeRole: '',
    },
  });

  const clientId = watch('clientId');
  const watchedClientRoles = watch('clientRoles');
  const isClientAccount = accountType === 'client';

  useEffect(() => {
    fetchClients();
    fetchRoles();
    fetchDefaultPermissions();
    fetchCurrentUserPermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCurrentUserPermissions = () => {
    const currentUser = sessionManager.getUser();

    if (isHeadCoach || isEmployee) {
      setCurrentUserPermissions(null);
      return;
    }

    if (currentUser && currentUser.permissions) {
      setCurrentUserPermissions(currentUser.permissions);
    } else {
      setCurrentUserPermissions(null);
    }
  };

  const fetchDefaultPermissions = async () => {
    try {
      const permissions = await fetchDefaultPermissionsFromBackend();
      if (permissions) {
        setBackendDefaultPermissions(permissions);
      } else {
        console.warn('⚠️ Using fallback default permissions (backend unavailable)');
      }
    } catch (error) {
      console.error('Error fetching default permissions:', error);
    }
  };

  useEffect(() => {
    if (editUser) {
      if (!showClientSelection && defaultClientId && editUser.clientId !== defaultClientId) {
        showErrorMsg('You can only edit users from your own organization.');
        onClose();
        return;
      }

      setAccountType(editUser.clientId ? 'client' : 'employee');
      reset({
        email: editUser.email || '',
        firstName: editUser.firstName || '',
        lastName: editUser.lastName || '',
        phone: editUser.phone || '',
        timezone: editUser.timezone || 'alaska',
        active: !!editUser.active,
        clientId: editUser.clientId || '',
        permissions: editUser.clientId
          ? {
              editUsers: !!editUser.permissions?.editUsers,
              editKpis: !!editUser.permissions?.editKpis,
              editPlayers: !!editUser.permissions?.editPlayers,
              editOpponents: !!editUser.permissions?.editOpponents,
              editSeasons: !!editUser.permissions?.editSeasons,
              editGames: !!editUser.permissions?.editGames,
            }
          : undefined,
        clientRoles: editUser.clientRoles || [],
        employeeRole: editUser.employeeRole || '',
      });

      if (editUser.customPermissions) {
        setCustomPermissions(editUser.customPermissions);
        setShowAdvancedPermissions(true);
      } else {
        setCustomPermissions(null);
        setShowAdvancedPermissions(false);
      }
    } else {
      setCustomPermissions(null);
      setShowAdvancedPermissions(false);
    }
  }, [editUser, reset, showClientSelection, defaultClientId, onClose]);

  useEffect(() => {
    if (watchedClientRoles && watchedClientRoles.length > 0) {
      const currentEditingRole = editUser?.clientRoles?.[0];
      const selectedRole = watchedClientRoles[0];

      if (!editUser || currentEditingRole !== selectedRole) {
        setCustomPermissions(null);
        setShowAdvancedPermissions(false);
      }
    }
  }, [watchedClientRoles, editUser]);

  const applyClientId = useCallback(
    (nextClientId: string) => {
      setValue('clientId', nextClientId, { shouldValidate: true });
    },
    [setValue],
  );

  const fetchClients = async () => {
    try {
      const response = await api.get(API_URLS.GET_ALL_ACTIVE_CLIENTS);
      if (response.data.success && response.data.total > 0) {
        setClients(response.data.data);
        if (!editUser && initialAccountType === 'client' && response.data.data.length > 0) {
          if (!showClientSelection) {
            if (defaultClientId) {
              applyClientId(defaultClientId);
            } else {
              const currentUserClientId = sessionManager.getClientId();
              applyClientId(currentUserClientId || response.data.data[0]._id);
            }
          } else {
            applyClientId(defaultClientId || response.data.data[0]._id);
          }
        } else if (editUser) {
          const match = response.data.data.find((c: any) => c._id === editUser.clientId);
          if (match) {
            applyClientId(match._id);
          }
        }
      }
    } catch (error) {
      showErrorMsg('Failed to fetch clients.');
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await api.get(API_URLS.GET_USER_ROLES);
      if (response.data.success && response.data.data) {
        setRoles(response.data.data);
      }
    } catch (error) {
      showErrorMsg('Failed to fetch roles.');
    }
  };

  useEffect(() => {
    if (editUser && clients.length > 0) {
      const found = clients.find((c) => c._id === editUser.clientId);
      if (found) {
        applyClientId(found._id);
      }
    }
  }, [editUser, clients, applyClientId]);

  const handleAccountTypeChange = (nextType: AccountType) => {
    if (!canChooseAccountType || nextType === accountType) return;

    setAccountType(nextType);
    setCustomPermissions(null);
    setShowAdvancedPermissions(false);

    if (nextType === 'employee') {
      setValue('clientId', '');
      setValue('clientRoles', []);
      return;
    }

    setValue('employeeRole', '');
    if (!clientId) {
      applyClientId(defaultClientId || clients[0]?._id || '');
    }
  };

  const handleClose = () => {
    if (!isLoading && !isSubmitting) {
      onClose();
    }
  };

  const onSubmit = async (data: UserFormData) => {
    const token = sessionManager.getToken();
    if (!token) {
      showErrorMsg('Authentication token not found. Please log in again.');
      return;
    }

    const isEmployeeAccount = accountType === 'employee';

    if (!isEmployeeAccount) {
      if (showClientSelection && !data.clientId) {
        showErrorMsg('Client information is required for client users');
        return;
      }

      if (!showClientSelection && defaultClientId && data.clientId !== defaultClientId) {
        showErrorMsg('You can only manage users from your own organization.');
        return;
      }
    }

    if (isEmployeeAccount && !canManageEmployees) {
      showErrorMsg("You don't have permission to manage employee accounts.");
      return;
    }

    setIsLoading(true);

    try {
      let response;
      const payload: any = {
        ...data,
        clientId: isEmployeeAccount ? '' : data.clientId,
        customPermissions,
      };

      if (editUser && editUser._id) {
        response = await api.put(API_URLS.UPDATE_USER, {
          ...payload,
          _id: editUser._id,
        });
        if (response.data.success) {
          showSuccessMsg('User updated successfully!');
          onUserAdded?.();
          onClose();
        } else {
          showErrorMsg(response.data.message || 'Failed to update user');
        }
      } else {
        response = await api.post(API_URLS.INVITE_USER, payload);
        if (response.data.success) {
          showSuccessMsg('User invitation sent successfully!');
          onUserAdded?.();
          onClose();
        } else {
          showErrorMsg(response.data.message || 'Failed to send user invitation');
        }
      }
    } catch (error: any) {
      console.error('Error adding/updating user:', error);

      if (error.response?.status === 401) {
        showErrorMsg('Session expired. Please log in again.');
      } else if (error.response?.data?.message) {
        showErrorMsg(error.response.data.message);
      } else if (error.message) {
        showErrorMsg(`Error: ${error.message}`);
      } else {
        showErrorMsg('Failed to add/update user. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const selectedClientName = clients.find((client) => client._id === (clientId || editUser?.clientId))?.name;

  return (
    <Modal isOpen={true} onClose={onClose} maxWidth="2xl" maxHeight="90vh" isLoading={isLoading || isSubmitting}>
      <ModalTitle>{editUser ? 'Edit User' : 'Invite User'}</ModalTitle>

      <ModalContent>
        <form onSubmit={handleSubmit(onSubmit)} id="user-form">
          {canChooseAccountType && (
            <div className="mb-6">
              <label className="block text-gray-700 font-medium mb-1">Account Type</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => handleAccountTypeChange('client')}
                  disabled={isLoading || isSubmitting}
                  className={`text-left border rounded-lg p-3 hover:bg-gray-50 transition-colors ${
                    isClientAccount ? 'border-green-500 bg-green-50' : 'border-gray-300'
                  }`}
                >
                  <span className="block font-medium text-gray-900">Client user</span>
                  <span className="mt-1 block text-sm text-gray-600">Invite a coach or staff member for a team.</span>
                </button>
                <button
                  type="button"
                  onClick={() => handleAccountTypeChange('employee')}
                  disabled={isLoading || isSubmitting}
                  className={`text-left border rounded-lg p-3 hover:bg-gray-50 transition-colors ${
                    !isClientAccount ? 'border-green-500 bg-green-50' : 'border-gray-300'
                  }`}
                >
                  <span className="block font-medium text-gray-900">Employee</span>
                  <span className="mt-1 block text-sm text-gray-600">Invite StatDog staff with internal access.</span>
                </button>
              </div>
            </div>
          )}

          {!canChooseAccountType && showClientSelection && (
            <div className="mb-4">
              <span
                className={`px-2 py-1 rounded-full text-sm ${
                  isClientAccount ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'
                }`}
              >
                {isClientAccount ? `Client user${selectedClientName ? ` · ${selectedClientName}` : ''}` : 'Employee'}
              </span>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-semibold mb-4">User Information</h3>

              {showClientSelection && isClientAccount && (
                <div className="mb-4">
                  <label className="block text-gray-700 font-medium mb-1">Client *</label>
                  <select
                    {...register('clientId')}
                    className={fieldClass(!!errors.clientId)}
                    disabled={isLoading || isSubmitting || !!editUser}
                  >
                    <option value="">Select a client</option>
                    {clients.map((client: any) => (
                      <option key={client._id} value={client._id}>
                        {client.name}
                      </option>
                    ))}
                  </select>
                  {errors.clientId && <p className="text-red-500 text-sm mt-1">{errors.clientId.message}</p>}
                </div>
              )}
              {(!showClientSelection || !isClientAccount) && <input type="hidden" {...register('clientId')} />}

              <div className="mb-4">
                <label className="block text-gray-700 font-medium mb-1">Email *</label>
                <input
                  type="email"
                  {...register('email')}
                  className={fieldClass(!!errors.email)}
                  disabled={isLoading || isSubmitting}
                />
                {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>}
              </div>

              <div className="mb-4">
                <label className="block text-gray-700 font-medium mb-1">First Name *</label>
                <input
                  type="text"
                  {...register('firstName')}
                  className={fieldClass(!!errors.firstName)}
                  disabled={isLoading || isSubmitting}
                />
                {errors.firstName && <p className="text-red-500 text-sm mt-1">{errors.firstName.message}</p>}
              </div>

              <div className="mb-4">
                <label className="block text-gray-700 font-medium mb-1">Last Name *</label>
                <input
                  type="text"
                  {...register('lastName')}
                  className={fieldClass(!!errors.lastName)}
                  disabled={isLoading || isSubmitting}
                />
                {errors.lastName && <p className="text-red-500 text-sm mt-1">{errors.lastName.message}</p>}
              </div>

              <div className="mb-4">
                <label className="block text-gray-700 font-medium mb-1">Phone *</label>
                <input
                  type="tel"
                  {...register('phone')}
                  className={fieldClass(!!errors.phone)}
                  disabled={isLoading || isSubmitting}
                />
                {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone.message}</p>}
              </div>

              <div className="mb-4">
                <label className="block text-gray-700 font-medium mb-1">Time Zone *</label>
                <select
                  {...register('timezone')}
                  className={fieldClass(!!errors.timezone)}
                  disabled={isLoading || isSubmitting}
                >
                  {TIMEZONES.map((timezone) => (
                    <option key={timezone.value} value={timezone.value}>
                      {timezone.label}
                    </option>
                  ))}
                </select>
                {errors.timezone && <p className="text-red-500 text-sm mt-1">{errors.timezone.message}</p>}
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="active"
                  {...register('active')}
                  className="mr-2"
                  disabled={isLoading || isSubmitting}
                />
                <label htmlFor="active" className="text-gray-700">
                  Active
                </label>
              </div>
            </div>

            <div>
              {isClientAccount ? (
                <>
                  <div className="mb-6">
                    <h3 className="font-semibold mb-4">Role</h3>
                    {!canEditThisUser && (
                      <div className="mb-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <p className="text-sm text-yellow-800">
                          <strong>Note:</strong> Only Head Coaches can edit another Head Coach's permissions.
                        </p>
                      </div>
                    )}
                    <div className="space-y-3">
                      {assignableRoles.map((role) => (
                        <div key={role._id} className="border rounded-lg p-3 hover:bg-gray-50 transition-colors">
                          <div className="flex items-start">
                            <Controller
                              name="clientRoles"
                              control={control}
                              render={({ field }) => {
                                const currentValues = field.value || [];
                                const isSelected = currentValues.includes(role._id);
                                return (
                                  <input
                                    type="radio"
                                    id={`client-role-${role._id}`}
                                    name="clientRole"
                                    value={role._id}
                                    checked={isSelected}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        field.onChange([role._id]);
                                      }
                                    }}
                                    className="mt-1 mr-3"
                                    disabled={isLoading || isSubmitting || !canEditThisUser}
                                  />
                                );
                              }}
                            />
                            <div className="flex-1">
                              <label
                                htmlFor={`client-role-${role._id}`}
                                className={`font-medium text-gray-900 cursor-pointer ${!canEditThisUser ? 'opacity-50' : ''}`}
                              >
                                {role.label}
                              </label>
                              {ROLE_DESCRIPTIONS[role.value] && (
                                <p className="text-sm text-gray-600 mt-1">{ROLE_DESCRIPTIONS[role.value]}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    {errors.clientRoles && <p className="text-red-500 text-sm mt-2">{errors.clientRoles.message}</p>}
                  </div>

                  {canManagePermissions && watchedClientRoles && watchedClientRoles.length > 0 && canEditThisUser && (
                    <div className="mb-6">
                      <h3 className="font-semibold mb-4">Permissions</h3>
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="flex-1">
                          <label className="text-sm font-medium text-gray-700">Custom Permissions</label>
                          <p className="text-xs text-gray-500 mt-1">
                            {showAdvancedPermissions
                              ? 'Override default role permissions with custom settings'
                              : 'Using default permissions for this role'}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            const newValue = !showAdvancedPermissions;
                            setShowAdvancedPermissions(newValue);
                            if (!newValue) {
                              setCustomPermissions(null);
                            }
                          }}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                            showAdvancedPermissions ? 'bg-blue-600' : 'bg-gray-300'
                          }`}
                          disabled={isLoading || isSubmitting}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              showAdvancedPermissions ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="mb-6">
                  <h3 className="font-semibold mb-4">Role</h3>
                  <div className="space-y-3">
                    {employeeRoles.map((role) => (
                      <div key={role._id} className="border rounded-lg p-3 hover:bg-gray-50 transition-colors">
                        <div className="flex items-center">
                          <Controller
                            name="employeeRole"
                            control={control}
                            render={({ field }) => (
                              <input
                                type="radio"
                                id={`employee-role-${role._id}`}
                                value={role._id}
                                checked={field.value === role._id}
                                onChange={field.onChange}
                                className="mr-3"
                                disabled={isLoading || isSubmitting}
                              />
                            )}
                          />
                          <label htmlFor={`employee-role-${role._id}`} className="text-gray-700">
                            {role.label}
                          </label>
                        </div>
                      </div>
                    ))}
                    {employeeRoles.length === 0 && (
                      <p className="text-sm text-gray-500">Employee roles will appear here once they finish loading.</p>
                    )}
                  </div>
                  {errors.employeeRole && <p className="text-red-500 text-sm mt-2">{errors.employeeRole.message}</p>}
                </div>
              )}
            </div>
          </div>

          {canManagePermissions &&
            isClientAccount &&
            watchedClientRoles &&
            watchedClientRoles.length > 0 &&
            showAdvancedPermissions &&
            canEditThisUser && (
              <div className="mt-6 border-t-2 border-gray-300 pt-6">
                <PermissionsEditor
                  roleValue={roles.find((r) => r._id === watchedClientRoles[0])?.value || 'statTaker'}
                  customPermissions={customPermissions}
                  onChange={(perms) => setCustomPermissions(perms)}
                  disabled={isLoading || isSubmitting}
                  backendDefaultPermissions={backendDefaultPermissions}
                  currentUserPermissions={currentUserPermissions}
                />
              </div>
            )}
        </form>
      </ModalContent>

      <ModalActions>
        <button
          type="button"
          onClick={handleClose}
          className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600 transition-colors disabled:opacity-50"
          disabled={isLoading || isSubmitting}
        >
          Cancel
        </button>
        <button
          type="submit"
          form="user-form"
          className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          disabled={isLoading || isSubmitting || !canEditThisUser}
        >
          {isLoading || isSubmitting ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              {editUser ? 'Saving...' : 'Sending Invite...'}
            </>
          ) : editUser ? (
            'Save Changes'
          ) : (
            'Send Invitation'
          )}
        </button>
      </ModalActions>
    </Modal>
  );
};

export default AddUserModal;
